/**
 * useAdminRevenue — Admin-Umsatzauswertung ueber alle Zahlungsplattformen.
 *
 * GET /api/v2/admin/revenue?days=60
 *
 * Loest den Legacy-Screen /admin/premium_payments ab. Alle Betraege in USD;
 * eine EUR-Umrechnung gibt es bewusst nicht mehr, weil sie historische Umsaetze
 * mit dem heutigen Tageskurs bewertet haette (und im Legacy-Code an einer
 * Stelle ohnehin falsch gerechnet hat).
 */
import { useQuery } from '@tanstack/react-query'
import i18n from '../../i18n'
import { adminFetch } from './adminFetch'

/** Umsatz eines Providers innerhalb eines Zeitraums. */
export type AdminRevenueProviderBucket = {
  total: number
  count: number
}

/**
 * Ein Zeitraum-Block. `providers` ist duenn besetzt — Plattformen ohne Umsatz
 * im Zeitraum fehlen schlicht, statt mit 0 dazustehen.
 */
export type AdminRevenueBucket = {
  from: string
  total: number
  count: number
  providers: Record<string, AdminRevenueProviderBucket>
}

/** Ein Tag der Zeitreihe. `providers` enthaelt hier nur die Summe je Plattform. */
export type AdminRevenueSeriesPoint = {
  date: string
  total: number
  count: number
  providers: Record<string, number>
}

export type AdminRevenueSeries = {
  days: number
  from: string
  to: string
  /** Legendenliste: alle im Fenster vorkommenden Plattformen, sortiert. */
  providers: string[]
  points: AdminRevenueSeriesPoint[]
}

/**
 * Die sieben Zeitraum-Schluessel. `last_*` sind rollierend (identisch zur
 * Legacy-Definition, damit die Zahlen vergleichbar bleiben), `calendar_*`
 * kalenderbasiert fuer die Buchhaltungssicht.
 */
export const REVENUE_BUCKET_KEYS = [
  'today',
  'yesterday',
  'last_24h',
  'last_7_days',
  'last_30_days',
  'calendar_week',
  'calendar_month',
] as const

export type AdminRevenueBucketKey = (typeof REVENUE_BUCKET_KEYS)[number]

export type AdminRevenueResponse = {
  currency: string
  time_zone: string
  generated_at: string
  buckets: Record<AdminRevenueBucketKey, AdminRevenueBucket>
  series: AdminRevenueSeries
}

/** Fenster der Zeitreihe. Fix, weil die Seite keinen Zeitraum-Umschalter hat. */
export const REVENUE_SERIES_DAYS = 60

export function useAdminRevenue(days: number = REVENUE_SERIES_DAYS) {
  const query = useQuery<AdminRevenueResponse>({
    queryKey: ['admin-revenue', days],
    queryFn: () =>
      adminFetch<AdminRevenueResponse>(
        '/api/v2/admin/revenue?days=' + days,
        i18n.t('adminRevenue:error.load'),
      ),
    retry: false,
    staleTime: 60000,
  })
  return {
    revenue: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}
